import { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useLocation, useNavigate, NavLink, Navigate, Outlet, Link } from 'react-router-dom';
import { PanelProvider, usePanelContext } from '../contexts/PanelContext';
import { ChatProvider } from '../contexts/ChatContext';
import toast from 'react-hot-toast';
import {
  Users,
  Calendar,
  Bug,
  PanelRight,
  MoreHorizontal,
} from 'lucide-react';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../lib/socket';
import { useProject, useProjects } from '../hooks/useProjects';
import { useProjectStore } from '../store/projectStore';
import { useOrgStore } from '../store/orgStore';
import { useOrgAutoSwitch } from '../hooks/useOrgAutoSwitch';
import { fetchUnread } from '../services/chatService';
import Spinner from '../components/ui/Spinner';
import DetailPanelRouter from '../components/layout/DetailPanelRouter';
import ChatSidePanel from '../components/chat/ChatSidePanel';
import { OrgAccessDeniedView } from '../components/org/OrgAccessDeniedView';
import { getProjectMenuItems, isFeatureAllowed } from '../constants/projectMenus';
import { useGitHubRepos } from '../hooks/useGitHub';

const TYPE_LABEL: Record<string, string> = {
  KANBAN: '칸반',
  SCRUM: '스크럼',
  CS: 'CS',
  TEAM: '팀 작업',
};

export default function ProjectDetailPage() {
  const { key } = useParams<{ key: string }>();
  const location = useLocation();
  const stateProjectId = (location.state as { projectId?: string })?.projectId;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const storedProject = useProjectStore((s) => s.currentProject);
  const { data: projects } = useProjects();

  // Resolve project ID: state > store > project list lookup by key
  const resolvedId = useMemo(() => {
    if (stateProjectId) return stateProjectId;
    if (storedProject?.projectKey === key && storedProject?.id) return storedProject.id;
    const found = projects?.find((p) => p.projectKey === key);
    return found?.id ?? '';
  }, [stateProjectId, storedProject, key, projects]);

  const { data: project, isLoading, error } = useProject(resolvedId);
  const { data: githubRepos } = useGitHubRepos(resolvedId);

  // CHG-149: 다른 조직 리소스 진입 시 활성 조직 자동 전환
  const autoSwitch = useOrgAutoSwitch();
  const organizations = useOrgStore((s) => s.organizations);
  const [accessDenied, setAccessDenied] = useState<{ orgName?: string } | null>(null);

  useEffect(() => {
    if (!project) return;
    const result = autoSwitch(project.organizationId);
    if (result.status === 'not-a-member') {
      const orgName = organizations.find((o) => o.id === result.targetOrgId)?.name;
      setAccessDenied({ orgName });
    } else {
      setAccessDenied(null);
    }
  }, [project, autoSwitch, organizations]);

  useEffect(() => {
    if (project) {
      setCurrentProject({
        id: project.id,
        name: project.name,
        projectKey: project.projectKey,
        projectType: project.projectType,
      });
    }
  }, [project, setCurrentProject]);

  // REQ-V31-016: 채팅 읽지 않은 메시지 상태
  const [hasChatUnread, setHasChatUnread] = useState(false);

  // 프로젝트 진입 시 unread 확인
  // 채팅 페이지에서는 clearChatUnread로 처리하므로 fetchUnread 생략 (레이스 컨디션 방지)
  useEffect(() => {
    if (!project?.chatEnabled || !project?.id) return;
    const segments = location.pathname.split('/');
    const kIdx = key ? segments.indexOf(key) : -1;
    if (kIdx >= 0 && segments[kIdx + 1] === 'chat') return;
    fetchUnread(project.id)
      .then(({ hasUnread }) => setHasChatUnread(hasUnread))
      .catch(() => {});
  }, [project?.id, project?.chatEnabled, location.pathname, key]);

  // Socket.io chat:unreadUpdate 리스닝
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !project?.id) return;

    const handleUnreadUpdate = ({ projectId: pid, hasUnread }: { projectId: string; hasUnread: boolean }) => {
      if (pid === project.id) {
        setHasChatUnread(hasUnread);
      }
    };

    socket.on(SOCKET_EVENTS.CHAT_UNREAD_UPDATE, handleUnreadUpdate);
    return () => {
      socket.off(SOCKET_EVENTS.CHAT_UNREAD_UPDATE, handleUnreadUpdate);
    };
  }, [project?.id]);

  const clearChatUnread = useCallback(() => setHasChatUnread(false), []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-red-500">프로젝트를 불러올 수 없습니다.</p>
        <Link to="/projects" className="text-sm text-primary-600 hover:underline">
          프로젝트 목록으로 돌아가기
        </Link>
      </div>
    );
  }

  // CHG-149: 다른 조직 소속 + 멤버 아님 → 권한 안내
  if (accessDenied) {
    return <OrgAccessDeniedView orgName={accessDenied.orgName} />;
  }

  const tabs = getProjectMenuItems(project.projectType, { enabledMenus: project.enabledMenus, hasGitHubRepos: (githubRepos?.length ?? 0) > 0 });

  // REQ-V17-007: CS 프로젝트 라우트 가드
  // /projects/:key/{feature} 에서 feature segment 추출
  const pathSegments = location.pathname.split('/');
  const keyIndex = pathSegments.indexOf(key!);
  const featureSegment = keyIndex >= 0 ? pathSegments[keyIndex + 1] : undefined;

  // REQ-V31-019: 프로젝트 진입 시 defaultMenu 리다이렉트
  if (!featureSegment) {
    let targetMenu = project.defaultMenu || 'dashboard';
    // enabledMenus가 있으면 defaultMenu가 비활성인지 확인
    if (project.enabledMenus && !project.enabledMenus.includes(targetMenu)) {
      targetMenu = 'dashboard';
    }
    const menuRoutes: Record<string, string> = {
      dashboard: 'dashboard',
      issues: 'issues',
      chat: 'chat',
      schedule: 'schedule',
      sprint: 'sprint',
    };
    return <Navigate to={`/projects/${key}/${menuRoutes[targetMenu] || 'dashboard'}`} replace />;
  }

  // CHG-055: enabledMenus 기반 접근 차단 (chatEnabled 포함 통합)
  if (featureSegment && !isFeatureAllowed(featureSegment, project.projectType, project.enabledMenus)) {
    toast.error('이 프로젝트에서 비활성화된 기능입니다.', { id: 'feature-guard' });
    return <Navigate to={`/projects/${key}/dashboard`} replace />;
  }

  // 레이아웃: AppLayout header h-14(56px) + main p-6(24px top+bottom=48px) = 104px
  const content = (
      <div className="flex flex-col" style={{ height: 'calc(100vh - 104px)' }}>
        {/* Project header */}
        <div className="shrink-0 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              <span className="text-sm font-medium text-gray-400 uppercase tracking-wider mt-1">{project.projectKey}</span>
              <span className="text-gray-200 mt-1">|</span>
              <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                <span className="flex items-center gap-1">
                  <Users className="size-3.5" />
                  멤버 {project.members.length}명
                </span>
                <span className="flex items-center gap-1">
                  <Bug className="size-3.5" />
                  작업 {project._count.issues}건
                </span>
                {project.startDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="size-3.5" />
                    {new Date(project.startDate).toLocaleDateString('ko-KR')}
                  </span>
                )}
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-blue-50 text-blue-700">
              {TYPE_LABEL[project.projectType] ?? project.projectType}
            </span>
          </div>
          {project.description && (
            <p className="text-sm text-gray-500 mt-1">{project.description}</p>
          )}
        </div>

        {/* Tab navigation */}
        <div className="shrink-0">
          <ProjectTabNav tabs={tabs} projectKey={key!} hasChatUnread={hasChatUnread} chatEnabled={project.chatEnabled} defaultMenu={project.defaultMenu} />
        </div>

        {/* Tab content + Side panel — 남은 공간 전부 차지, 넘치지 않음 */}
        <ProjectContentArea project={project} clearChatUnread={clearChatUnread} />
      </div>
  );

  return (
    <PanelProvider projectId={project.id}>
      {project.chatEnabled ? (
        <ChatProvider projectId={project.id}>{content}</ChatProvider>
      ) : (
        content
      )}
    </PanelProvider>
  );
}

// ─── 탭 컨텐츠 + 상세 패널 영역 (PanelContext 소비) ───
function ProjectContentArea({
  project,
  clearChatUnread,
}: {
  project: import('../services/projectService').ProjectDetail;
  clearChatUnread: () => void;
}) {
  const { detailPanel, isDetailVisible, isChatVisible, closeDetailPanel } = usePanelContext();
  // 라우트 변경 시 패널 닫기는 PanelContext의 URL ?panel 동기화로 처리
  // (이전엔 pathname 변경 시 강제로 닫았는데, 그 로직이 URL→state 동기화와 충돌해 뒤로가기 복원을 막았음)

  // REQ-V34-008: ESC 키로 상세 패널 닫기 (채팅 패널은 ESC로 닫지 않음)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && detailPanel) {
        closeDetailPanel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [detailPanel, closeDetailPanel]);

  return (
    <div className="flex flex-1 min-h-0">
      <div className="flex-1 min-w-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Outlet context={{ project, clearChatUnread }} />
      </div>

      {/* 채팅 사이드 패널 */}
      {isChatVisible && project.chatEnabled && (
        <ChatSidePanel projectId={project.id} onVisible={clearChatUnread} />
      )}

      {/* 상세 패널 (PanelContext 기반) */}
      {isDetailVisible && detailPanel && (
        <DetailPanelRouter
          detailPanel={detailPanel}
          projectId={project.id}
          projectKey={project.projectKey}
          members={project.members}
          workflowStatuses={project.workflow?.statuses}
          projectWorkflow={project.workflow}
          onClose={closeDetailPanel}
        />
      )}
    </div>
  );
}

// ─── 탭 네비게이션 (PanelContext 소비) ───
function ProjectTabNav({
  tabs,
  projectKey,
  hasChatUnread,
  chatEnabled,
  defaultMenu,
}: {
  tabs: import('../constants/projectMenus').ProjectMenuItem[];
  projectKey: string;
  hasChatUnread: boolean;
  chatEnabled: boolean;
  defaultMenu?: string | null;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatPanelEnabled, toggleChatPanel } = usePanelContext();

  // REQ-V34-004: 채팅 탭 클릭 시 사이드 패널 모드 분기
  const handleChatTabClick = useCallback(
    (e: React.MouseEvent) => {
      if (!chatPanelEnabled) return; // 패널 off → 기존 동작 (NavLink가 처리)

      e.preventDefault();

      // 현재 채팅 라우트에 있으면 기본 메뉴로 이동
      const segments = location.pathname.split('/');
      const keyIdx = segments.indexOf(projectKey);
      const currentFeature = keyIdx >= 0 ? segments[keyIdx + 1] : undefined;

      if (currentFeature === 'chat') {
        const target = defaultMenu && defaultMenu !== 'chat' ? defaultMenu : 'issues';
        navigate(`/projects/${projectKey}/${target}`);
      }
      // 다른 탭에 있으면 현재 탭 유지 (사이드 패널이 이미 열려있음)
    },
    [chatPanelEnabled, location.pathname, projectKey, defaultMenu, navigate],
  );

  // ─── 오버플로우 측정 + 더보기 메뉴 ───────────────────────────
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const widthsCache = useRef<number[]>([]);
  const [cutoff, setCutoff] = useState<number>(tabs.length);
  const [showMore, setShowMore] = useState(false);
  const moreBtnRef = useRef<HTMLDivElement | null>(null);

  // 모든 탭의 natural width 를 캐시 (탭 개수가 변할 때만 재측정)
  useLayoutEffect(() => {
    const widths: number[] = [];
    for (let i = 0; i < tabs.length; i++) {
      const el = tabRefs.current[i];
      if (el) widths[i] = el.offsetWidth;
    }
    if (widths.length > 0) widthsCache.current = widths;
  }, [tabs.length]);

  // 컨테이너 너비 + 캐시된 widths 로 cutoff 계산
  useLayoutEffect(() => {
    const recompute = () => {
      const container = containerRef.current;
      if (!container) return;
      const containerWidth = container.clientWidth;
      const widths = widthsCache.current;
      if (widths.length === 0) {
        setCutoff(tabs.length);
        return;
      }

      const GAP = 2;            // gap-0.5
      const MORE_BTN_WIDTH = 90; // 더보기 버튼 + 카운트 reserve
      let total = 0;
      let newCutoff = tabs.length;
      for (let i = 0; i < tabs.length; i++) {
        const w = widths[i] ?? 0;
        const isLast = i === tabs.length - 1;
        const need = total + w + (isLast ? 0 : MORE_BTN_WIDTH + GAP);
        if (need > containerWidth) {
          newCutoff = i;
          break;
        }
        total += w + GAP;
      }
      setCutoff(newCutoff);
    };

    recompute();
    if (!containerRef.current) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [tabs.length]);

  // 더보기 드롭다운 닫기 — 바깥 클릭 / ESC
  useEffect(() => {
    if (!showMore) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (moreBtnRef.current && !moreBtnRef.current.contains(target)) {
        setShowMore(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMore(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showMore]);

  const renderTab = (tab: (typeof tabs)[number], opts: { ref?: (el: HTMLAnchorElement | null) => void; onNavigate?: () => void }) => {
    const Icon = tab.icon;
    const isChatTab = tab.key === 'chat';
    return (
      <NavLink
        key={tab.key}
        ref={opts.ref}
        to={tab.to}
        end={tab.end}
        onClick={(e) => {
          if (isChatTab) handleChatTabClick(e);
          opts.onNavigate?.();
        }}
        className={({ isActive }) =>
          `flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
            isActive
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`
        }
      >
        <Icon className="size-4" />
        {tab.label}
        {isChatTab && hasChatUnread && (
          <span className="ml-1 bg-red-500 text-white text-[11px] font-bold rounded-full w-4 h-4 inline-flex items-center justify-center leading-none">
            N
          </span>
        )}
        {/* REQ-V34-003: 채팅 탭 옆 사이드 패널 토글 */}
        {isChatTab && chatEnabled && (
          <button
            type="button"
            className={`ml-1 p-0.5 rounded transition-colors ${
              chatPanelEnabled
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleChatPanel();
            }}
            title={chatPanelEnabled ? '사이드 패널 닫기' : '사이드 패널 열기'}
          >
            <PanelRight className="w-3.5 h-3.5" />
          </button>
        )}
      </NavLink>
    );
  };

  const overflowTabs = tabs.slice(cutoff);

  return (
    <div ref={containerRef} className="relative border-b border-gray-200 mb-6">
      <nav className="flex gap-0.5 -mb-px items-center">
        {/* 탭 영역만 클리핑 — 드롭다운은 외곽까지 자유롭게 떠야 함 */}
        <div className="flex gap-0.5 overflow-hidden flex-1 min-w-0">
          {tabs.slice(0, cutoff).map((tab, i) =>
            renderTab(tab, { ref: (el) => { tabRefs.current[i] = el; } }),
          )}

          {/* 초기 측정용 — cutoff 가 줄어서 일부 탭이 사라져도 widths 캐시는 첫 렌더 때 잡힘. */}
          {widthsCache.current.length === 0 && tabs.slice(cutoff).map((tab, i) =>
            renderTab(tab, { ref: (el) => { tabRefs.current[cutoff + i] = el; } }),
          )}
        </div>

        {overflowTabs.length > 0 && (
          <div ref={moreBtnRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => setShowMore(!showMore)}
              className={`flex items-center gap-1 px-1.5 py-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                showMore
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              aria-label={`더보기 (${overflowTabs.length})`}
            >
              <MoreHorizontal className="size-4" />
              더보기
              <span className="text-[11px] text-gray-400">({overflowTabs.length})</span>
            </button>
            {showMore && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white shadow-lg rounded-lg border border-gray-200 py-1 min-w-[200px] max-h-[60vh] overflow-y-auto">
                {overflowTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isChatTab = tab.key === 'chat';
                  return (
                    <NavLink
                      key={tab.key}
                      to={tab.to}
                      end={tab.end}
                      onClick={(e) => {
                        if (isChatTab) handleChatTabClick(e);
                        setShowMore(false);
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`
                      }
                    >
                      <Icon className="size-4 shrink-0" />
                      <span className="truncate">{tab.label}</span>
                      {isChatTab && hasChatUnread && (
                        <span className="ml-auto bg-red-500 text-white text-[11px] font-bold rounded-full w-4 h-4 inline-flex items-center justify-center leading-none">
                          N
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>
    </div>
  );
}

export function ProjectTabPlaceholder({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-48 bg-white border border-gray-200 rounded-xl">
      <p className="text-gray-400">{title} 기능은 준비 중입니다.</p>
    </div>
  );
}
